import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Sparkles,
  Package,
  ImageIcon,
  Bot,
  ChevronRight,
  BarChart3,
  Video,
  ArrowRight,
  Plus,
} from 'lucide-react';
import Sidebar from '../../components/sidebar/Sidebar';
import { TaskyHeaderActions } from '../../components/tasky/layout/TaskyHeaderActions';
import CreditPurchaseModal from '../../components/credit/CreditPurchaseModal';
import { useLogout } from '../../hooks/useLogout';
import { RootState } from '../../redux/store';
import { fetchNews, NewsArticle, NewsListResponse } from '../../services/api/newsService';
import { fetchUserCreditBalance, CreditBalance } from '../../services/api/creditService';
import trialService, { TrialStatusResponse } from '../../services/api/trialService';
import { fetchRecentWorks, RecentWorkItem } from '../../services/api/recentWorksService';
import { fetchHomeSummary, HomeSummary } from '../../services/api/homeSummaryService';
import onboardingV2Service, { OnboardingV2Progress } from '../../services/api/onboardingV2Service';
import {
  trackMetaAddPaymentInfo,
  trackMetaStartTrial,
  trackMetaSubscribe,
} from '../../utils/metaPixel';
import { trackXAddPaymentInfo, trackXSubscribe } from '../../utils/xPixel';
import RecentWorkDetailModal from '../../components/recent-works/RecentWorkDetailModal';
import UpdateAnnouncementModal from '../../components/updates/UpdateAnnouncementModal';
import NewsPopupModal from '../../components/updates/NewsPopupModal';
import CelebrationConfetti from '../../components/common/CelebrationConfetti';
import InviteMemberButton from '../../components/organization/InviteMemberButton';
import FriendInviteEntry from '../../components/invite/FriendInviteEntry';
import { selectIsUserLeader } from '../../redux/organization/organizationSlice';
import { hasOrganization, isAdminActor } from '../../utils/authDestination';
import {
  TaskyCard,
  TaskyProgress,
  ResponsiveAppShell,
} from '../../components/tasky';
import { reviewSafeText, useMetaLang, HOME_STRINGS, MetaLang } from '../../i18n/metaReviewLang';
import CreationGuide from './CreationGuide';
import OnboardingChecklist, {
  getAttractingQuickActionIndex,
  isOnboardingChecklistVisible,
} from './OnboardingChecklist';

interface QuickAction {
  icon: React.ReactNode;
  title: string;
  description: string;
  path: string;
}

interface RecentItem {
  id: string;
  title: string;
  subtitle: string;
  updatedLabel: string;
  actionLabel: string;
  iconBg: string;
  iconColor: string;
  icon: React.ReactNode;
  path: string;
}

const formatShortDate = (value: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${m}/${day}`;
};

const getTodayLabel = (lang: MetaLang): string => {
  const d = new Date();
  if (lang === 'en') {
    return d.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' });
  }
  const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日（${weekdays[d.getDay()]}）`;
};

const getGreeting = (t: (typeof HOME_STRINGS)['ja']): string => {
  const h = new Date().getHours();
  if (h < 5) return t.greetingEvening;
  if (h < 11) return t.greetingMorning;
  if (h < 18) return t.greetingAfternoon;
  return t.greetingEvening;
};

// Stripe Checkout の subscription_data.metadata.plan で渡しているスラッグから
// 画面表示用のプラン名を引く。新プランを追加する場合はここに対応を追加する。
const PLAN_DISPLAY_NAMES: Record<string, string> = {
  personal: 'PERSONAL',
  team: 'TEAM',
  agency: 'AGENCY',
};

// trialStatus/onboardingProgress の取得試行中に LEFT カラムへ表示するプレースホルダー。
// 「今すぐ始める」グリッドと全面版オンボーディングのどちらが出るか確定するまでの
// レイアウトフラッシュを防ぐためのスケルトン。
const OnboardingLeftSkeleton: React.FC = () => (
  <div aria-hidden="true" className="animate-pulse space-y-5">
    <div className="h-4 w-24 rounded bg-warm-surface" />
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="min-h-[112px] rounded-tasky-md border border-warm-border bg-warm-surface"
        />
      ))}
    </div>
  </div>
);

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [lang] = useMetaLang();
  const t = HOME_STRINGS[lang];
  const handleLogout = useLogout();
  const user = useSelector((state: RootState) => state.auth.user);
  const isLeader = useSelector(selectIsUserLeader);
  const [isCreditPurchaseOpen, setIsCreditPurchaseOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [credit, setCredit] = useState<CreditBalance | null>(null);
  const [trialStatus, setTrialStatus] = useState<TrialStatusResponse | null>(null);
  const [recentWorks, setRecentWorks] = useState<RecentWorkItem[]>([]);
  const [homeSummary, setHomeSummary] = useState<HomeSummary | null>(null);
  const [onboardingProgress, setOnboardingProgress] = useState<OnboardingV2Progress | null>(null);
  // trialStatus/onboardingProgress の取得試行(成功・失敗いずれも)が完了したかどうか。
  // 組織ありユーザーがこの2つを取得し終えるまで LEFT カラムをスケルトン表示し、
  // 「今すぐ始める」→ 全面版オンボーディングへのレイアウトフラッシュを防ぐ。
  // 失敗時も finally で必ず true にするため無限ローディングにはならない。
  const [trialStatusLoaded, setTrialStatusLoaded] = useState(false);
  const [onboardingProgressLoaded, setOnboardingProgressLoaded] = useState(false);
  const [selectedWork, setSelectedWork] = useState<RecentWorkItem | null>(null);
  // Stripe Checkout 成功直後に /home?subscription=success で遷移してきた際に
  // 「{プラン名}に昇格しました」モーダルを出すための state。
  const [welcomePlan, setWelcomePlan] = useState<string | null>(null);
  // オンボーディングボーナス(商品登録・バナー生成・プラン選択(クレカ登録))が
  // claim で新規付与された瞬間に紙吹雪でお祝いするためのフラグ。付与判定は
  // 下の loadOnboardingProgress の claim レスポンス granted_now が担う。
  const [celebrationActive, setCelebrationActive] = useState(false);

  // Stripe Checkout success_url で渡される `?subscription=success` を検知し、
  // ウェルカムモーダルを表示する。session_id も合わせて URL から削除し、
  // ブラウザリロードや戻る操作でモーダルが再表示されないようにする。
  //
  // プラン名は URL の `?plan=` (backend が success_url に埋め込む) から取得する。
  // redux の user.organization.plan は Stripe webhook 反映ラグの間古い値のまま
  // になり、PERSONAL→TEAM 等のプラン変更時に旧プランを誤表示する可能性がある
  // ため、Stripe からのリダイレクト時点で確定している plan を URL 経由で受け
  // 取って表示する (Cursor/Codex PR #486 [P2] 指摘への対応)。
  // フォールバック: 古いセッションで plan クエリが無い場合のみ redux 値を見る。
  useEffect(() => {
    if (searchParams.get('subscription') !== 'success') return;

    const planFromUrl = searchParams.get('plan');
    const plan = planFromUrl ?? user?.organization?.plan ?? 'personal';
    const sessionId = searchParams.get('session_id');
    const trialParam = searchParams.get('trial');
    setWelcomePlan(plan);

    const addPaymentEventId = trackMetaAddPaymentInfo({ plan, sessionId, user });
    trackXAddPaymentInfo(addPaymentEventId, plan);
    if (trialParam === '0' || trialParam === 'false') {
      const subscribeEventId = trackMetaSubscribe({ plan, sessionId, user });
      trackXSubscribe(subscribeEventId, plan);
    } else {
      const parsedTrialDays = trialParam ? Number(trialParam) : undefined;
      trackMetaStartTrial({
        plan,
        trialDays: Number.isFinite(parsedTrialDays) ? parsedTrialDays : undefined,
        sessionId,
        user,
      });
    }

    // クエリパラメータを除去 (履歴に残さないよう replace)
    const next = new URLSearchParams(searchParams);
    next.delete('subscription');
    next.delete('plan');
    next.delete('session_id');
    next.delete('trial');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, user]);

  useEffect(() => {
    fetchNews(1)
      .then((response) => {
        const articles = Array.isArray(response)
          ? response
          : ((response as NewsListResponse)?.data || []);
        setNews(articles.slice(0, 5));
      })
      .catch((err) => {
        console.error('Failed to load news:', err);
      });
  }, []);

  useEffect(() => {
    // 組織未所属ユーザー (オンボーディング未完了など) は credit 機能対象外。
    const organizationId = user?.organization_id ?? user?.organization?.id ?? null;
    if (!organizationId) return;
    fetchUserCreditBalance()
      .then((data) => setCredit(data))
      .catch((err) => console.error('Failed to load credits:', err));
  }, [user?.organization_id, user?.organization?.id]);

  useEffect(() => {
    trialService
      .getTrialStatus()
      .then((data) => setTrialStatus(data))
      .catch((err) => console.error('Failed to load trial status:', err))
      .finally(() => setTrialStatusLoaded(true));
  }, []);

  useEffect(() => {
    const organizationId = user?.organization_id ?? user?.organization?.id ?? null;
    if (!organizationId) {
      // 組織なしユーザーはオンボーディング進捗の対象外。即座に「取得完了」扱いにして
      // ローディングゲートで固まらないようにする(通常レイアウトへ)。
      setOnboardingProgressLoaded(true);
      return undefined;
    }

    let cancelled = false;
    const loadOnboardingProgress = async () => {
      try {
        const claimResponse = await onboardingV2Service.claim();
        if (!cancelled) {
          setOnboardingProgress(claimResponse.progress);
          // プラン選択(クレカ登録)・商品登録・バナー生成のいずれかのボーナスが
          // この claim で新たに付与されたら紙吹雪でお祝いする。granted_now は
          // 「今回付与された」ものだけ true になる冪等フラグなので、2回目以降の
          // ロードでは全て false になり演出は再発火しない。
          // Stripe Checkout 成功で /home?subscription=success に戻ってきた直後も
          // この経路を通り、granted_now.plan で紙吹雪が出る(ウェルカムモーダルとは
          // z-index が独立し pointer-events-none のため共存できる)。
          const { granted_now: grantedNow } = claimResponse;
          if (grantedNow.plan || grantedNow.product || grantedNow.generate) {
            setCelebrationActive(true);
          }
        }
      } catch (claimError) {
        console.error('Failed to claim onboarding bonuses:', claimError);
        try {
          const progress = await onboardingV2Service.getProgress();
          if (!cancelled) {
            setOnboardingProgress(progress);
          }
        } catch (progressError) {
          console.error('Failed to load onboarding progress:', progressError);
        }
      } finally {
        // claim・getProgress の両方が失敗しても必ず完了扱いにする
        // (onboardingProgress は null のままなので showOnboardingFull は自然に false になり
        // 通常レイアウトへフォールバックする。無限ローディングにはしない)。
        if (!cancelled) {
          setOnboardingProgressLoaded(true);
        }
      }
    };

    void loadOnboardingProgress();

    return () => {
      cancelled = true;
    };
  }, [user?.organization_id, user?.organization?.id]);

  useEffect(() => {
    fetchRecentWorks(1, 4, undefined, lang)
      .then((res) => setRecentWorks(res.data || []))
      .catch((err) => console.error('Failed to load recent works:', err));
  }, [lang]);

  // 組織の有無は authDestination.hasOrganization に一本化する（判定のズレ防止）。
  const userHasOrganization = hasOrganization(user);
  const isUserLoaded = user != null;
  // 管理者は組織を持たず会社登録もできないので、組織なしでも setup へは送らない
  const userIsAdmin = isAdminActor(user);

  useEffect(() => {
    fetchHomeSummary()
      .then((data) => setHomeSummary(data))
      .catch((err) => {
        console.error('Failed to load home summary:', err);
        // 組織未所属だと backend は 404 を返す。401 ではないので axios の
        // interceptor は動かず、何も出ない画面がそのまま残る（本番で観測）。
        // 会社情報入力へ送って詰みを解消する。
        // user 未読（リロード直後の過渡状態）では組織なしと決めつけず、
        // backend の文言が一致したときだけ遷移する。
        const status = err?.response?.status;
        const message = String(err?.response?.data?.message ?? '');
        if (
          status === 404 &&
          !userIsAdmin &&
          (message.includes('組織に所属していません') || (isUserLoaded && !userHasOrganization))
        ) {
          navigate('/signup/setup', { replace: true });
        }
      });
  }, [navigate, isUserLoaded, userHasOrganization, userIsAdmin]);

  const typeToIcon: Record<RecentWorkItem['type'], { icon: React.ReactNode; iconBg: string; iconColor: string; label: string }> = {
    chat: { icon: <Bot className="w-4 h-4" />, iconBg: 'bg-primary/10', iconColor: 'text-primary', label: t.typeChat },
    image: { icon: <ImageIcon className="w-4 h-4" />, iconBg: 'bg-primary/10', iconColor: 'text-primary', label: t.typeImage },
    video: { icon: <Video className="w-4 h-4" />, iconBg: 'bg-purple/10', iconColor: 'text-purple', label: t.typeVideo },
    manga: { icon: <BarChart3 className="w-4 h-4" />, iconBg: 'bg-success/10', iconColor: 'text-success', label: t.typeManga },
    lp: { icon: <Sparkles className="w-4 h-4" />, iconBg: 'bg-success/10', iconColor: 'text-success', label: t.typeLp },
    music: { icon: <Sparkles className="w-4 h-4" />, iconBg: 'bg-purple/10', iconColor: 'text-purple', label: t.typeMusic },
  };

  const formatRelativeTime = (iso: string): string => {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return t.now;
    if (m < 60) return `${m}${t.minutesAgo}`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}${t.hoursAgo}`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}${t.daysAgo}`;
    return new Date(iso).toLocaleDateString(lang === 'en' ? 'en-US' : 'ja-JP');
  };

  const rawUserGivenName =
    user?.name && user.name !== '未設定' && user.name !== '名称未設定'
      ? user.name.split(/\s|　/)[0]
      : user?.email
        ? user.email.split('@')[0]
        : t.defaultUser;
  const userGivenName = reviewSafeText(rawUserGivenName, lang, lang === 'en' ? 'Review User' : t.defaultUser);

  const quickActions: QuickAction[] = [
    {
      icon: <Package className="w-5 h-5 text-primary" />,
      title: t.qaProductTitle,
      description: t.qaProductDesc,
      path: '/knowledge',
    },
    {
      icon: <ImageIcon className="w-5 h-5 text-primary" />,
      title: t.qaBannerTitle,
      description: t.qaBannerDesc,
      path: '/ads/banner',
    },
    {
      icon: <Sparkles className="w-5 h-5 text-primary" />,
      title: t.qaAssetTitle,
      description: t.qaAssetDesc,
      path: '/ads/video',
    },
    {
      icon: <Bot className="w-5 h-5 text-primary" />,
      title: t.qaAgentTitle,
      description: t.qaAgentDesc,
      path: '/chat/ai',
    },
  ];

  const attractingQuickActionIndex = getAttractingQuickActionIndex(onboardingProgress);
  // 3ステップ未完了 + トライアル中の間はメイン領域に全面版を表示し、
  // 「今すぐ始める」「最近の作業」とサイドバー版カードを非表示にする(OnboardingChecklist と同一条件)。
  const showOnboardingFull = isOnboardingChecklistVisible(
    onboardingProgress,
    trialStatus?.is_in_trial ?? false,
  );
  // 組織ありユーザーに限り、trialStatus/onboardingProgress の取得試行が完了するまで
  // LEFT カラムをスケルトン表示する(「今すぐ始める」→全面版のちらつき防止)。
  // 組織なしユーザーは対象外の useEffect が即座に loaded=true にするため、ここには来ない。
  const isOnboardingGateLoading =
    userHasOrganization && (!trialStatusLoaded || !onboardingProgressLoaded);

  const totalBalance = credit?.total_balance ?? 0;
  const monthlyLimit = credit?.wallets?.monthly?.limit ?? 0;
  const planLimit = monthlyLimit > 0 ? monthlyLimit : Math.max(totalBalance, 10000);
  const progressPct = planLimit > 0
    ? Math.min(100, Math.round((totalBalance / planLimit) * 1000) / 10)
    : 0;

  return (
    <ResponsiveAppShell
      sidebar={(
        <Sidebar
          onLogout={handleLogout}
          onToggleCollapse={setSidebarCollapsed}
          collapsed={sidebarCollapsed}
        />
      )}
      mobileSidebar={(
        <Sidebar
          onLogout={handleLogout}
          onToggleCollapse={() => undefined}
          collapsed={false}
        />
      )}
      topbarTitle={t.home}
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col lg:h-screen lg:min-h-0">
        {/* Topbar */}
        <header className="hidden h-14 flex-shrink-0 items-center justify-between border-b border-warm-border bg-white px-6 lg:flex">
          <div>
            <p className="text-base font-bold text-dark leading-tight">{t.home}</p>
            <p className="text-xs text-gray-400">{getTodayLabel(lang)}</p>
          </div>
          <div className="flex items-center gap-2.5">
            {trialStatus?.is_in_trial && !trialStatus.has_subscription && (
              <button
                type="button"
                onClick={() => navigate('/plans')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  trialStatus.days_remaining <= 2
                    ? 'bg-amber-500 text-white hover:bg-amber-600'
                    : 'bg-success/10 text-success hover:bg-success/20 border border-success/20'
                }`}
                aria-label="Trial days remaining"
              >
                <span>{t.trialRemaining(trialStatus.days_remaining)}</span>
                <ArrowRight className="w-3 h-3" />
              </button>
            )}
            <InviteMemberButton isLeader={isLeader} onClick={() => navigate('/organization')} />
            {/* 友だち紹介。組織内招待（上）とは別物で、権限・プランに左右されない。 */}
            <FriendInviteEntry />
            <TaskyHeaderActions />
          </div>
        </header>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="p-4 sm:p-6">
            {/* Greeting */}
            <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xl font-bold text-dark leading-tight">
                  {lang === 'en'
                    ? `${getGreeting(t)}, ${userGivenName} 👋`
                    : `${getGreeting(t)}、${userGivenName}${t.greetingSuffix} 👋`}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {t.tagline}
                </p>
              </div>
            </div>

            <CreationGuide lang={lang} />

            {/* 2-column */}
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
              {/* LEFT */}
              <div className="flex-1 min-w-0 space-y-5">
                {isOnboardingGateLoading ? (
                  <OnboardingLeftSkeleton />
                ) : showOnboardingFull && onboardingProgress && trialStatus ? (
                  <OnboardingChecklist
                    isInTrial={trialStatus.is_in_trial}
                    onNavigate={navigate}
                    progress={onboardingProgress}
                    variant="full"
                  />
                ) : (
                  <>
                    {/* Quick Actions */}
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-3">
                        {t.quickStart}
                      </p>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-tutorial="home-quick-actions">
                        {quickActions.map((a, index) => (
                          <button
                            key={a.title}
                            type="button"
                            onClick={() => navigate(a.path)}
                            className={`min-h-[112px] cursor-pointer rounded-tasky-md border bg-white p-4 text-left transition hover:-translate-y-px hover:shadow-card sm:p-5 ${
                              index === attractingQuickActionIndex
                                ? 'obd-attract border-success/50'
                                : 'border-warm-border'
                            }`}
                          >
                            <div className="w-9 h-9 rounded-tasky-sm bg-warm-surface flex items-center justify-center mb-3">
                              {a.icon}
                            </div>
                            <p className="text-sm font-bold text-dark mb-1">{a.title}</p>
                            <p className="text-xs text-gray-500 leading-relaxed">{a.description}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Recent Work */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                          {t.recentWork}
                        </p>
                        <button
                          type="button"
                          onClick={() => navigate('/recent-works')}
                          className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          {t.seeAll}
                        </button>
                      </div>
                      <TaskyCard variant="bordered" padding="none" className="overflow-hidden" data-tutorial="home-recent-work">
                        <div
                          className="hidden items-center bg-warm-surface px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 sm:grid sm:border-b sm:border-warm-border"
                          style={{ gridTemplateColumns: '32px 1fr 80px 56px' }}
                        >
                          <span />
                          <span>{t.projectName}</span>
                          <span className="text-right">{t.updatedAt}</span>
                          <span />
                        </div>
                        {recentWorks.length === 0 ? (
                          <div className="px-4 py-8 text-sm text-center text-gray-400">
                            {t.noRecentWork}
                          </div>
                        ) : (
                          recentWorks.map((item, idx) => {
                            const meta = typeToIcon[item.type] || typeToIcon.chat;
                            return (
                          <button
                            key={`${item.type}-${item.id}`}
                            type="button"
                            onClick={() => setSelectedWork(item)}
                            className={`flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-warm-hover sm:grid ${
                              idx < recentWorks.length - 1 ? 'border-b border-warm-border' : ''
                            }`}
                            style={{ gridTemplateColumns: '32px 1fr 80px 56px', gap: '12px' }}
                          >
                            <div className={`w-7 h-7 rounded-tasky-sm ${meta.iconBg} flex items-center justify-center flex-shrink-0 ${meta.iconColor}`} title={meta.label}>
                              {meta.icon}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-dark truncate">
                                {reviewSafeText(item.type === 'image' ? `${formatShortDate(item.updated_at)} ${t.typeImage} #${item.id.slice(0, 8)}` : item.title, lang, `${meta.label} ${idx + 1}`)}
                              </p>
                              <p className="text-xs text-gray-400 truncate">
                                <span className="font-semibold text-gray-500">{meta.label}</span>
                                {item.subtitle ? ` · ${reviewSafeText(item.subtitle, lang, meta.label)}` : ''}
                              </p>
                            </div>
                            <p className="hidden text-right text-xs text-gray-400 whitespace-nowrap sm:block">{formatRelativeTime(item.updated_at)}</p>
                            <div className="hidden text-right sm:block">
                              <span className="text-xs font-semibold text-gray-500 bg-warm-surface border border-warm-border px-2.5 py-1 rounded-full whitespace-nowrap">
                                {t.detail}
                              </span>
                            </div>
                          </button>
                            );
                          })
                        )}
                      </TaskyCard>
                    </div>
                  </>
                )}
              </div>

              {/* RIGHT */}
              <div className="w-full flex-shrink-0 space-y-5 lg:w-[268px]">
                {/* sidebar 版 OnboardingChecklist は表示条件が showOnboardingFull と同一
                    (isOnboardingChecklistVisible) のため、!showOnboardingFull の間は常に
                    null を返すだけになる。ここでは描画しない (full 版は LEFT カラムが担当)。 */}

                {/* News */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                      {t.news}
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/news')}
                      className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {t.seeAll}
                    </button>
                  </div>
                  <TaskyCard variant="bordered" padding="none" className="overflow-hidden">
                    {news.length === 0 ? (
                      <div className="px-4 py-6 text-xs text-center text-gray-400">
                        {t.noNews}
                      </div>
                    ) : (
                      news.map((article, idx) => (
                        <button
                          key={article.id}
                          type="button"
                          onClick={() => navigate(`/news/${article.id}`)}
                          className={`flex items-start justify-between gap-2.5 px-4 py-3 w-full text-left hover:bg-warm-hover transition-colors cursor-pointer ${
                            idx < news.length - 1 ? 'border-b border-warm-border' : ''
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-[10px] text-gray-400 font-medium whitespace-nowrap">
                                {formatShortDate(article.published_at || article.created_at)}
                              </span>
                              {idx === 0 && (
                                <span className="text-[9px] font-bold bg-dark text-white px-2 py-0.5 rounded-full tracking-wide">
                                  NEW
                                </span>
                              )}
                            </div>
                            <p className="text-xs font-semibold text-dark leading-relaxed line-clamp-2">
                              {reviewSafeText(article.title, lang, `Product update ${idx + 1}`)}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" />
                        </button>
                      ))
                    )}
                  </TaskyCard>
                </div>

                {/* Credit Balance */}
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-3">
                    {t.creditBalance}
                  </p>
                  <TaskyCard variant="bordered" padding="sm" data-tutorial="home-credit-balance">
                    <div className="flex items-baseline gap-1.5 mb-2">
                      <span className="text-3xl font-bold text-dark leading-none">
                        {totalBalance.toLocaleString()}
                      </span>
                      <span className="text-xs text-gray-400">/ {planLimit.toLocaleString()}</span>
                    </div>
                    <TaskyProgress value={progressPct} className="mb-2" />
                    <div className="grid grid-cols-3 gap-0 mt-4 pt-3 border-t border-warm-border text-center">
                      <div className="border-r border-warm-border pr-2">
                        <p className="text-lg font-bold text-dark leading-none">
                          {(homeSummary?.monthly_banner_count ?? 0).toLocaleString()}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1">{t.monthlyBanners}</p>
                      </div>
                      <div className="border-r border-warm-border px-2">
                        <p className="text-lg font-bold text-dark leading-none">
                          {(homeSummary?.monthly_video_count ?? 0).toLocaleString()}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1">{t.monthlyVideos}</p>
                      </div>
                      <div className="pl-2">
                        <p className="text-lg font-bold text-dark leading-none">
                          {(homeSummary?.product_count ?? 0).toLocaleString()}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1">{t.products}</p>
                      </div>
                    </div>
                    {/* 追加購入できるのは組織リーダーのみ。ヘッダーの CreditBadge と
                     * 同じ CreditPurchaseModal を開くので導線が二重にならない。 */}
                    {isLeader && (
                      <div className="mt-3 pt-3 border-t border-warm-border">
                        <button
                          type="button"
                          onClick={() => setIsCreditPurchaseOpen(true)}
                          className="w-full flex items-center justify-center gap-1.5 rounded-tasky-sm px-3 py-2 text-[13px] font-medium text-indigo-500 transition-colors hover:bg-[#F5F3FF]"
                        >
                          <Plus className="w-4 h-4" />
                          {t.addCredits}
                        </button>
                      </div>
                    )}
                  </TaskyCard>
                </div>
              </div>
            </div>

            <div className="h-8" />
          </div>
        </div>
      </div>

      <CreditPurchaseModal
        isOpen={isCreditPurchaseOpen}
        onClose={() => setIsCreditPurchaseOpen(false)}
      />

      <RecentWorkDetailModal item={selectedWork} onClose={() => setSelectedWork(null)} />
      <UpdateAnnouncementModal />
      <NewsPopupModal />
      <CelebrationConfetti
        active={celebrationActive}
        onComplete={() => setCelebrationActive(false)}
      />

      {/* Stripe Checkout 成功直後のウェルカムモーダル */}
      {welcomePlan && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="subscription-welcome-title"
          onClick={() => setWelcomePlan(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center">
                <svg className="w-7 h-7 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2
              id="subscription-welcome-title"
              className="text-center text-[20px] font-bold text-dark mb-2"
            >
              {(PLAN_DISPLAY_NAMES[welcomePlan] ?? welcomePlan.toUpperCase()) + ' プランに昇格しました'}
            </h2>
            <p className="text-center text-[14px] text-dark/70 mb-5">
              さっそく最初のバナーを作ってみましょう。
            </p>
            <ol className="mb-6 space-y-2">
              {['商品を追加する', 'スタイルを選ぶ（未選択なら自動適応）', 'バナーを生成する'].map(
                (step, i) => (
                  <li key={i} className="flex items-center gap-3 text-[14px] text-dark/80">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-success/10 text-success text-[13px] font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    {step}
                  </li>
                )
              )}
            </ol>
            <button
              type="button"
              onClick={() => {
                setWelcomePlan(null);
                navigate('/knowledge');
              }}
              className="w-full h-11 rounded-full bg-success text-white text-[15px] font-semibold hover:opacity-90 transition-opacity"
            >
              商品を追加してバナーを作る
            </button>
            <button
              type="button"
              onClick={() => setWelcomePlan(null)}
              className="mt-2 w-full h-10 rounded-full bg-transparent text-dark/60 text-[14px] font-medium hover:text-dark transition-colors"
            >
              あとで
            </button>
          </div>
        </div>
      )}
    </ResponsiveAppShell>
  );
};

export default HomePage;
